import { loadPdfjs } from "./pdfjs.js";
import { API_BASE, saveAdminTestExplanations } from "./api.js";

const TITULACIONES = [
  { key: "CY", label: "Capitán de Yate", header: /EXAMEN\s+DE\s+CAPIT[ÁA]N\s+DE\s+YATE/i },
  { key: "PY", label: "Patrón de Yate", header: /EXAMEN\s+DE\s+PATR[ÓO]N\s+DE\s+YATE/i },
  { key: "PER", label: "Patrón de Embarcaciones de Recreo", header: /EXAMEN\s+DE\s+PATR[ÓO]N\s+DE\s+EMBARCACIONES\s+DE\s+RECREO(?!\s*\(Con\s+PNB\s+Liberado\))/i },
  { key: "PER_PNB", label: "PER (Con PNB Liberado)", header: /EXAMEN\s+DE\s+PATR[ÓO]N\s+DE\s+EMBARCACIONES\s+DE\s+RECREO\s*\(Con\s+PNB\s+Liberado\)/i },
  { key: "PNB", label: "Patrón para Navegación Básica", header: /EXAMEN\s+DE\s+PATR[ÓO]N\s+PARA\s+NAVEGACI[ÓO]N\s+B[ÁA]SICA/i }
];

const TOPICS = [
  "Nomenclatura náutica.",
  "Elementos de amarre y fondeo.",
  "Seguridad.",
  "Seguridad en la mar",
  "Legislación.",
  "Balizamiento.",
  "Reglamento (RIPA).",
  "Maniobra y navegación.",
  "Emergencias en la mar.",
  "Meteorología",
  "Teoría de navegación",
  "Cálculo de navegación",
  "Navegación carta",
  "Inglés",
  "Teoría de la navegación.",
  "Carta de navegación."
];

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function stripFooterGunk(line) {
  let t = String(line || "")
    .replace(/SUBDIRECCI[ÓO]N\s+GENERAL.*$/i, "")
    .replace(/^\s*ÁREA\s+FUNCIONAL\s+DE\s+N[ÁA]UTICA\s+DE\s+RECREO\s*$/i, "")
    .replace(/^\s*AREA\s+FUNCIONAL\s+DE\s+NAUTICA\s+DE\s+RECREO\s*$/i, "")
    .trim();

  t = t
    .replace(/(?:\s+|^)(?:Y\s+)?MINISTERIO\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?SECRETAR[ÍI]A\s+GENERAL\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?DIRECCI[ÓO]N\s+GENERAL\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?DE\s+TRANSPORTES\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?MOVILIDAD\s+SOSTENIBLE\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?TRANSPORTES\s+A[ÉE]REO\s+Y\s+MAR[ÍI]TIMO\b[\s\S]*$/i, "")
    .replace(/(?:\s+|^)(?:Y\s+)?MARINA\s+MERCANTE\b[\s\S]*$/i, "")
    .trim();

  return t;
}

function normalize(s) {
  let t = String(s || "");

  // Normalize compatibility glyphs (helps with some PDF outputs)
  try {
    t = t.normalize("NFKC");
  } catch {
    // ignore
  }

  t = t
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200F\uFEFF\u202A-\u202E\u2066-\u2069]/g, "")
    .replaceAll("\\u00ad", "")
    .replace(/\u00ad/g, "")
    // Observed odd glyph in some PDFs representing "Fi"
    .replace(/𿿿/g, "Fi")
    // Common ligatures from PDF text extraction
    .replace(/\ufb00/g, "ff")
    .replace(/\ufb01/g, "fi")
    .replace(/\ufb02/g, "fl")
    .replace(/\ufb03/g, "ffi")
    .replace(/\ufb04/g, "ffl")
    .replace(/\ufb05/g, "ft")
    .replace(/\ufb06/g, "st")
    // Drop control chars (keep \n and \t)
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, " ")
    // Remove replacement character often produced by bad glyph mapping
    .replace(/\ufffd/g, "")
    .replace(/[（]/g, "(")
    .replace(/[）]/g, ")")
    .replace(/[．]/g, ".")
    .replace(/[，]/g, ",")
    .replace(/[：]/g, ":")
    .replace(/[；]/g, ";")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\bS\s+ECRETAR[ÍI]A\b/g, "SECRETARÍA")
    .replace(/\\r/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+\\n/g, "\\n")
    .replace(/\\n{3,}/g, "\\n\\n")
    .trim();

  try {
    t = t.replace(/\p{Cf}+/gu, "");
    t = t.replace(/\p{Co}+/gu, "");
  } catch {
    // ignore
  }

  // Fix hyphenation split by whitespace: "ilumina- ción" -> "iluminación"
  try {
    t = t.replace(/(\p{L}{2,})-\s+(\p{L}{2,})/gu, (m, a, b) => {
      const b0 = String(b).charAt(0);
      return b0 === b0.toLowerCase() ? `${a}${b}` : m;
    });
  } catch {
    t = t.replace(/([A-Za-zÁÉÍÓÚÜÑáéíóúüñ]{2,})-\s+([A-Za-zÁÉÍÓÚÜÑáéíóúüñ]{2,})/g, (m, a, b) => {
      const b0 = String(b).charAt(0);
      return b0 === b0.toLowerCase() ? `${a}${b}` : m;
    });
  }

  return t.replace(/\b(?:[A-Za-zÁÉÍÓÚÑáéíóúñ]\s+){2,}[A-Za-zÁÉÍÓÚÑáéíóúñ]\b/g, (m) => m.replace(/\s+/g, ""));
}

function sampleLinesContaining(text, needles, limit = 8) {
  const lines = String(text || "").split("\n");
  const out = [];
  for (const ln of lines) {
    const l = ln.trim();
    if (!l) continue;
    const hit = needles.some((n) => l.toLowerCase().includes(String(n).toLowerCase()));
    if (hit) out.push(l);
    if (out.length >= limit) break;
  }
  return out;
}

function splitSectionsByTitulation(text) {
  const out = [];
  for (const t of TITULACIONES) {
    const re = new RegExp(t.header.source, "ig");
    let m;
    while ((m = re.exec(text)) !== null) {
      out.push({ idx: m.index, tit: t.key, label: t.label });
    }
  }
  out.sort((a, b) => a.idx - b.idx);
  const sections = [];
  for (let i = 0; i < out.length; i++) {
    const start = out[i].idx;
    const end = i + 1 < out.length ? out[i + 1].idx : text.length;
    sections.push({ ...out[i], chunk: text.slice(start, end) });
  }
  return sections;
}

function findTestCode(chunk) {
  const m = String(chunk || "").match(/C[oó]digo(?:\s+de)?\s+Tes[t]?\s+(\d+)/i);
  return m ? String(m[1]).padStart(2, "0") : null;
}

function parseExamQuestions(chunk) {
  chunk = normalize(chunk);
  chunk = chunk.replace(/\n--\s*\d+\s+of\s+\d+\s*--\n/g, "\n");

  const lines = chunk
    .split("\n")
    .map((l) => stripFooterGunk(l.trim()))
    .filter(Boolean);

  function splitGluedOptionLine(line) {
    const m = line.match(/^([a-dA-D])\s*[).]\s*(.+?)([.?!])\s+(\d{1,2})\s+([A-ZÁÉÍÓÚÑ¿].+)$/);
    if (!m) return [line];
    const optKey = m[1].toLowerCase();
    const optText = (m[2] + m[3]).trim();
    const qLine = `${m[4]} ${m[5]}`.trim();
    return [`${optKey}) ${optText}`, qLine];
  }

  function splitMultipleOptionsInLine(line) {
    const t = String(line || "").trim();
    if (!t) return [];
    const re = /(^|[\s.?!:;])([a-dA-D])\s*[).]\s*/g;
    const hits = [];
    let m;
    while ((m = re.exec(t)) !== null) {
      const idx = m.index + (m[1] ? 1 : 0);
      hits.push({ idx, key: m[2].toLowerCase() });
    }
    if (hits.length <= 1) return [t];
    hits.sort((a, b) => a.idx - b.idx);
    const out = [];
    for (let i = 0; i < hits.length; i++) {
      const start = hits[i].idx;
      const end = i + 1 < hits.length ? hits[i + 1].idx : t.length;
      const part = t.slice(start, end).trim();
      if (!part) continue;
      out.push(part.replace(/^([a-dA-D])\s*[.]/, "$1)"));
    }
    return out.length ? out : [t];
  }

  const expanded = [];
  for (const l of lines) {
    const parts = splitGluedOptionLine(l);
    for (const p of parts) {
      const opts = splitMultipleOptionsInLine(p);
      for (const o of opts) expanded.push(o);
    }
  }

  const questions = [];
  let currentTopic = null;
  let q = null;
  let expectingPromptForNum = null;
  let pendingPromptPrefix = null;

  const isTopicLine = (line) => {
    const t = TOPICS.find((tp) => tp.toLowerCase() === line.toLowerCase());
    return t ? t.replace(/\.$/, "") : null;
  };

  const optRe = /^[-–•]?\s*([a-dA-D])\s*[).]\s*(.*)$/;
  const qStartRe = /^(\d{1,2})\s*[.)-]?\s+(?=[¿A-ZÁÉÍÓÚÑ])(.+)$/;
  const qNumOnlyRe = /^(\d{1,2})$/;

  function finalizeQ() {
    if (!q) return;
    const byKey = new Map((q.options || []).map((o) => [o.key, o]));
    q.options = ["a", "b", "c", "d"].map((k) => byKey.get(k)).filter(Boolean);
    delete q.__hasD;
    questions.push(q);
    q = null;
  }

  for (let i = 0; i < expanded.length; i++) {
    const line = expanded[i];
    const next = expanded[i + 1] || "";

    if (/^EXAMEN\s+DE/i.test(line)) continue;
    if (/^C[oó]digo/i.test(line)) continue;
    if (/^MINISTERIO/i.test(line)) continue;
    if (/^SECRETAR[ÍI]A/i.test(line)) continue;
    if (/^DIRECCI[ÓO]N/i.test(line)) continue;

    const tp = isTopicLine(line);
    if (tp) {
      if (q && q.__hasD) finalizeQ();
      currentTopic = tp;
      pendingPromptPrefix = null;
      continue;
    }

    const om = line.match(optRe);
    if (om) {
      if (!q) continue;
      const key = om[1].toLowerCase();
      const text = (om[2] || "").trim();
      if (!["a", "b", "c", "d"].includes(key)) continue;
      const existing = (q.options || []).find((o) => o.key === key);
      if (existing) existing.text = (existing.text + " " + text).trim();
      else q.options.push({ key, text });
      if (key === "d") q.__hasD = true;
      continue;
    }

    if (expectingPromptForNum == null) {
      const mNumOnlyNext = next.match(qNumOnlyRe);
      if (mNumOnlyNext) {
        const n = Number(mNumOnlyNext[1]);
        if (Number.isFinite(n) && n >= 1 && n <= 99) {
          let optAt = -1;
          const maxLookahead = 10;
          for (let k = i + 2; k < expanded.length && k <= i + 1 + maxLookahead; k++) {
            if (optRe.test(expanded[k])) {
              optAt = k;
              break;
            }
            if (qStartRe.test(expanded[k])) break;
            if (qNumOnlyRe.test(expanded[k]) && k !== i + 1) break;
          }
          if (optAt !== -1) {
            if (q) finalizeQ();
            const cont = expanded.slice(i + 2, optAt).join(" ").trim();
            const prompt = cont ? (line + " " + cont).trim() : line;
            i = optAt - 1;
            q = { n, topic: currentTopic, prompt, options: [] };
            continue;
          }
        }
      }
    }

    const nmOnly = line.match(qNumOnlyRe);
    if (nmOnly) {
      const n = Number(nmOnly[1]);
      if (Number.isFinite(n) && n >= 1 && n <= 99) {
        expectingPromptForNum = n;
        if (!q && pendingPromptPrefix == null) {
          const prev = expanded[i - 1] || "";
          const prevIsOpt = optRe.test(prev);
          const prevIsTopic = !!isTopicLine(prev);
          const prevIsHeader =
            /^EXAMEN\s+DE/i.test(prev) ||
            /^C[oó]digo/i.test(prev) ||
            /^MINISTERIO/i.test(prev) ||
            /^SECRETAR[ÍI]A/i.test(prev) ||
            /^DIRECCI[ÓO]N/i.test(prev);
          if (prev && !prevIsOpt && !prevIsTopic && !prevIsHeader) pendingPromptPrefix = prev;
        }
      }
      continue;
    }

    const qm = line.match(qStartRe);
    if (qm) {
      const n = Number(qm[1]);
      const prompt = (qm[2] || "").trim();
      if (Number.isFinite(n) && n >= 1 && n <= 99) {
        finalizeQ();
        q = { n, topic: currentTopic, prompt, options: [] };
        expectingPromptForNum = null;
        continue;
      }
    }

    if (expectingPromptForNum != null) {
      finalizeQ();
      const prompt = pendingPromptPrefix ? (pendingPromptPrefix + " " + line).trim() : line;
      q = { n: expectingPromptForNum, topic: currentTopic, prompt, options: [] };
      expectingPromptForNum = null;
      pendingPromptPrefix = null;
      continue;
    }

    if (q) {
      if ((q.options || []).length === 0) q.prompt = (q.prompt ? q.prompt + " " : "") + line;
      else {
        const last = q.options[q.options.length - 1];
        if (last && last.text) last.text = (last.text + " " + line).trim();
      }
    } else {
      pendingPromptPrefix = line;
    }
  }

  finalizeQ();
  return questions;
}

function parseAnswerTemplates(text) {
  text = normalize(text);
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const out = [];
  const codeRe = /C[oó]digo(?:\s+de)?\s+Tes[t]?\s+(\d+)/i;
  const titleRe = /(Respuestas\s+al\s+)?EXAMEN\s+DE/i;

  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(codeRe);
    if (!m) continue;
    const code = String(m[1]).padStart(2, "0");

    let titleLine = null;
    let titleIdx = -1;
    for (let j = i; j >= 0 && j >= i - 10; j--) {
      if (titleRe.test(lines[j])) {
        titleLine = lines[j];
        titleIdx = j;
        break;
      }
    }
    if (!titleLine) continue;
    let titleNorm = titleLine.replace(/^Respuestas\s+al\s+/i, "").trim();

    const nextLineAfterTitle = titleIdx >= 0 && titleIdx + 1 < lines.length ? lines[titleIdx + 1] : "";
    if (/^\(.*\)$/i.test(nextLineAfterTitle) && /pnb/i.test(nextLineAfterTitle)) {
      titleNorm = `${titleNorm} ${nextLineAfterTitle}`.replace(/\s+/g, " ").trim();
    }

    const answers = {};
    let pendingLetter = null;
    for (let k = i + 1; k < lines.length; k++) {
      if (codeRe.test(lines[k]) || titleRe.test(lines[k])) break;
      const mNB = lines[k].match(/^(\d{1,2})\s+([A-D])$/);
      if (mNB) {
        answers[Number(mNB[1])] = mNB[2];
        pendingLetter = null;
        continue;
      }
      const mL = lines[k].match(/^([A-D])$/);
      if (mL) {
        pendingLetter = mL[1];
        continue;
      }
      const mN = lines[k].match(/^(\d{1,2})$/);
      if (mN && pendingLetter) {
        answers[Number(mN[1])] = pendingLetter;
        pendingLetter = null;
      }
    }

    out.push({ title: titleNorm, code, answers });
  }

  return out;
}

function titleToTitulationKey(title) {
  const t = String(title || "").toLowerCase();
  if (t.includes("capit") && t.includes("yate")) return "CY";
  if (t.includes("patr") && t.includes("yate") && !t.includes("capit")) return "PY";
  if (t.includes("embarcaciones de recreo") && (t.includes("pnb") || t.includes("con pnb liberado") || t.includes("pnb liberado"))) return "PER_PNB";
  if (t.includes("embarcaciones de recreo")) return "PER";
  if (t.includes("navegación básica") || t.includes("navegacion basica")) return "PNB";
  return "UNKNOWN";
}

/** Id estable (01–11) para GET /api/temario/:id — los PDF ya no se sirven como estáticos del admin. */
function temarioIdFor(topic, tit) {
  if (!["PER", "PER_PNB", "PNB"].includes(tit)) return null;
  const t = String(topic || "").toLowerCase();
  if (t.includes("nomenclatura")) return "01";
  if (t.includes("amarre") || t.includes("fondeo")) return "02";
  if (t.includes("seguridad")) return "03";
  if (t.includes("legisl")) return "04";
  if (t.includes("baliz")) return "05";
  if (t.includes("ripa") || t.includes("reglamento")) return "06";
  if (t.includes("maniobra")) return "07";
  if (t.includes("emergencias")) return "08";
  if (t.includes("meteorolog")) return "09";
  if (t.includes("teoría") || t.includes("teoria")) return "10";
  if (t.includes("carta")) return "11";
  return null;
}

function stripAccents(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

const STOP = new Set([
  "de",
  "del",
  "la",
  "el",
  "los",
  "las",
  "un",
  "una",
  "unos",
  "unas",
  "y",
  "o",
  "u",
  "en",
  "por",
  "para",
  "que",
  "se",
  "su",
  "sus",
  "al",
  "a",
  "e",
  "con",
  "sin",
  "como",
  "sobre",
  "entre",
  "es",
  "son",
  "ser",
  "no",
  "si",
  "mas",
  "más",
  "muy",
  "tambien",
  "también"
]);

function toks(s) {
  const t = stripAccents(String(s || "").toLowerCase()).replace(/[^a-z0-9áéíóúñü\s]/gi, " ");
  return new Set(
    t
      .split(/\s+/)
      .filter((w) => w.length >= 3 && !STOP.has(w))
  );
}

async function loadPdfTextByPage(pdfjsLib, url, fetchInit) {
  const res = await fetch(url, fetchInit);
  if (!res.ok) throw new Error(`No se pudo leer temario: ${url} (${res.status})`);
  const buf = await res.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const items = content.items
      .filter((it) => it && typeof it.str === "string" && it.str.trim() !== "")
      .map((it) => {
        const tr = it.transform || [1, 0, 0, 1, 0, 0];
        return { s: it.str, x: tr[4] || 0, y: tr[5] || 0 };
      });
    const byY = new Map();
    for (const it of items) {
      const y = Math.round(it.y * 2) / 2;
      if (!byY.has(y)) byY.set(y, []);
      byY.get(y).push(it);
    }
    const ys = [...byY.keys()].sort((a, b) => b - a);
    const lines = [];
    for (const y of ys) {
      const row = byY.get(y).sort((a, b) => a.x - b.x);
      const line = row
        .map((r) => r.s)
        .join(" ")
        .replace(/\s{2,}/g, " ")
        .trim();
      if (line) lines.push(line);
    }
    pages.push(lines.join("\n"));
  }
  return pages;
}

function paragraphsFromPageText(txt) {
  return String(txt || "")
    .replace(/\u00ad/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter((p) => p.length >= 80);
}

function scorePara(querySet, para) {
  const ps = toks(para);
  let score = 0;
  for (const w of querySet) if (ps.has(w)) score++;
  return score;
}

async function pdfToText(pdfjsLib, file) {
  const buf = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: buf });
  const pdf = await loadingTask.promise;
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const rawItems = content.items
      .filter((it) => it && typeof it.str === "string" && it.str.trim() !== "")
      .map((it) => {
        const tr = it.transform || [1, 0, 0, 1, 0, 0];
        return { s: it.str, x: tr[4] || 0, y: tr[5] || 0 };
      });

    const items = rawItems;
    const byY = new Map();
    for (const it of items) {
      const y = Math.round(it.y * 2) / 2;
      if (!byY.has(y)) byY.set(y, []);
      byY.get(y).push(it);
    }
    const ys = [...byY.keys()].sort((a, b) => b - a);
    const lines = [];
    for (const y of ys) {
      const row = byY.get(y).sort((a, b) => a.x - b.x);
      const line = row
        .map((r) => r.s)
        .join(" ")
        .replace(/\s{2,}/g, " ")
        .trim();
      if (!line) continue;
      if (/SUBDIRECCI[ÓO]N\s+GENERAL\s+DE\s+SEGURIDAD/i.test(line)) continue;
      if (/CONTAMINACI[ÓO]N\s+E\s+INSPECCI[ÓO]N\s+MAR[ÍI]TIMA/i.test(line)) continue;
      if (/ÁREA\s+FUNCIONAL\s+DE\s+N[ÁA]UTICA\s+DE\s+RECREO/i.test(line)) continue;
      if (/\bDE\s+TRANSPORTES\b/i.test(line) && /\bMOVILIDAD\s+SOSTENIBLE\b/i.test(line)) continue;
      if (/\bTRANSPORTES\s+A[ÉE]REO\s+Y\s+MAR[ÍI]TIMO\b/i.test(line)) continue;
      if (/\bMARINA\s+MERCANTE\b/i.test(line)) continue;
      if (/\bSECRETAR[ÍI]A\s+GENERAL\b/i.test(line)) continue;
      if (/\bMINISTERIO\b/i.test(line)) continue;
      if (/\bDIRECCI[ÓO]N\s+GENERAL\b/i.test(line) && /\bMARINA\s+MERCANTE\b/i.test(line)) continue;
      lines.push(line);
    }
    parts.push(lines.join("\n"));
  }
  return parts.join("\n");
}

async function pdfToTextByPageForFile(pdfjsLib, file) {
  const buf = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: buf });
  const pdf = await loadingTask.promise;
  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const rawItems = content.items
      .filter((it) => it && typeof it.str === "string" && it.str.trim() !== "")
      .map((it) => {
        const tr = it.transform || [1, 0, 0, 1, 0, 0];
        return { s: it.str, x: tr[4] || 0, y: tr[5] || 0 };
      });

    const byY = new Map();
    for (const it of rawItems) {
      const y = Math.round(it.y * 2) / 2;
      if (!byY.has(y)) byY.set(y, []);
      byY.get(y).push(it);
    }
    const ys = [...byY.keys()].sort((a, b) => b - a);
    const lines = [];
    for (const y of ys) {
      const row = byY.get(y).sort((a, b) => a.x - b.x);
      const line = row
        .map((r) => r.s)
        .join(" ")
        .replace(/\s{2,}/g, " ")
        .trim();
      if (!line) continue;
      if (/SUBDIRECCI[ÓO]N\s+GENERAL\s+DE\s+SEGURIDAD/i.test(line)) continue;
      if (/CONTAMINACI[ÓO]N\s+E\s+INSPECCI[ÓO]N\s+MAR[ÍI]TIMA/i.test(line)) continue;
      if (/ÁREA\s+FUNCIONAL\s+DE\s+N[ÁA]UTICA\s+DE\s+RECREO/i.test(line)) continue;
      if (/\bDE\s+TRANSPORTES\b/i.test(line) && /\bMOVILIDAD\s+SOSTENIBLE\b/i.test(line)) continue;
      if (/\bTRANSPORTES\s+A[ÉE]REO\s+Y\s+MAR[ÍI]TIMO\b/i.test(line)) continue;
      if (/\bMARINA\s+MERCANTE\b/i.test(line)) continue;
      if (/\bSECRETAR[ÍI]A\s+GENERAL\b/i.test(line)) continue;
      if (/\bMINISTERIO\b/i.test(line)) continue;
      if (/\bDIRECCI[ÓO]N\s+GENERAL\b/i.test(line) && /\bMARINA\s+MERCANTE\b/i.test(line)) continue;
      lines.push(line);
    }
    pages.push(lines.join("\n"));
  }
  return { pdf, pages };
}

function detectExamSectionsByPage(pages) {
  const out = [];
  if (!Array.isArray(pages) || !pages.length) return out;

  const examHeaderRe = /EXAMEN\s+DE/i;
  for (let i = 0; i < pages.length; i++) {
    const t = String(pages[i] || "");
    if (!examHeaderRe.test(t)) continue;
    out.push({ startPageIdx: i });
  }
  if (!out.length) return [];

  const sections = [];
  for (let i = 0; i < out.length; i++) {
    const startPageIdx = out[i].startPageIdx;
    const endPageIdx = i + 1 < out.length ? out[i + 1].startPageIdx - 1 : pages.length - 1;
    const chunk = pages.slice(startPageIdx, endPageIdx + 1).join("\n\n");

    const titHit =
      TITULACIONES.find((tt) => tt.header.test(chunk)) ||
      TITULACIONES.find((tt) => tt.header.test(String(pages[startPageIdx] || ""))) ||
      null;
    const tit = titHit ? titHit.key : null;
    const label = titHit ? titHit.label : null;
    const code = findTestCode(chunk);

    sections.push({ startPageIdx, endPageIdx, chunk, tit, label, code });
  }
  return sections;
}

function findLastQuestionPageIdx({ pages, questions }) {
  const maxN = Math.max(...(questions || []).map((q) => Number(q && q.n)).filter((n) => Number.isFinite(n)));
  if (!Number.isFinite(maxN) || maxN <= 0) return null;

  const re = new RegExp(`(^|\\n)\\s*${maxN}\\s*[.)-]`, "m");
  for (let i = 0; i < (pages || []).length; i++) {
    if (re.test(normalize(String(pages[i] || "")))) return i;
  }
  // Fallback: assume last question appears near end of section.
  return Math.max(0, (pages || []).length - 1);
}

async function renderPdfPagesToImages({ pdf, pageNumbers, scale = 1.6, quality = 0.8 }) {
  const out = [];
  for (const n of pageNumbers) {
    const page = await pdf.getPage(n);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d", { alpha: false });
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    out.push({
      kind: "pageImage",
      page: n,
      mime: "image/jpeg",
      width: canvas.width,
      height: canvas.height,
      dataUrl
    });
  }
  return out;
}

const SPANISH_MONTHS_PIPE =
  "enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre";

function titleCaseEsMonth(word) {
  const w = String(word || "").toLowerCase();
  if (!w) return "";
  return w.charAt(0).toUpperCase() + w.slice(1);
}

function inferMonthYearFromFileName(fileName) {
  const n = String(fileName || "").toLowerCase();
  const re = new RegExp(`\\b(${SPANISH_MONTHS_PIPE})\\b[^0-9]{0,5}(20\\d{2})`, "i");
  const m = n.match(re);
  if (m) return { month: titleCaseEsMonth(m[1]), year: Number(m[2]) };
  const reGlue = new RegExp(`\\b(${SPANISH_MONTHS_PIPE})(20\\d{2})\\b`, "i");
  const mg = n.match(reGlue);
  if (mg) return { month: titleCaseEsMonth(mg[1]), year: Number(mg[2]) };
  const re2 = new RegExp(`(20\\d{2})[^a-z]{0,5}\\b(${SPANISH_MONTHS_PIPE})\\b`, "i");
  const m2 = n.match(re2);
  if (m2) return { month: titleCaseEsMonth(m2[2]), year: Number(m2[1]) };
  return null;
}

function inferMonthYearFromEnText(enText) {
  const head = String(enText || "").slice(0, 16000);
  const re = new RegExp(`\\b(${SPANISH_MONTHS_PIPE})\\b\\s+de\\s+(20\\d{2})\\b`, "i");
  const m = head.match(re);
  if (m) return { month: titleCaseEsMonth(m[1]), year: Number(m[2]) };
  const reGlue = new RegExp(`\\b(${SPANISH_MONTHS_PIPE})(20\\d{2})\\b`, "i");
  const mg = head.match(reGlue);
  if (mg) return { month: titleCaseEsMonth(mg[1]), year: Number(mg[2]) };
  const re2 = new RegExp(`\\b(${SPANISH_MONTHS_PIPE})\\b[\\s\\S]{0,80}?\\b(20\\d{2})\\b`, "i");
  const m2 = head.match(re2);
  if (m2) return { month: titleCaseEsMonth(m2[1]), year: Number(m2[2]) };
  const re3 = new RegExp(`\\b(20\\d{2})\\b[\\s\\S]{0,80}?\\b(${SPANISH_MONTHS_PIPE})\\b`, "i");
  const m3 = head.match(re3);
  if (m3) return { month: titleCaseEsMonth(m3[2]), year: Number(m3[1]) };
  return null;
}

function resolveImportMonthYear({ formMonth, formYear, pdfEnunciados, pdfRespuestas, enText }) {
  let month = String(formMonth || "").trim();
  const rawY = Number(formYear);
  let year = !Number.isFinite(rawY) || rawY <= 0 ? null : rawY;

  const inferred =
    inferMonthYearFromFileName(pdfEnunciados && pdfEnunciados.name) ||
    inferMonthYearFromFileName(pdfRespuestas && pdfRespuestas.name) ||
    inferMonthYearFromEnText(enText);

  const formMonthEmpty = !month;

  if (inferred) {
    // Se o mês não foi indicado no form, a data vem do PDF/ficheiro: usar mês e ano inferidos (não o ano por defeito do UI).
    if (formMonthEmpty) {
      month = inferred.month || month;
      year = inferred.year != null ? inferred.year : year;
    } else {
      if (year == null) year = inferred.year != null ? inferred.year : year;
    }
  }

  return { month: month || null, year };
}

// (API-only admin) versioning/active é decidido no servidor, não no browser.

function buildImports({ month, year, enText, ansText, log }) {
  const enSections = splitSectionsByTitulation(enText);
  const ansBlocks = parseAnswerTemplates(ansText);

  log(`Secciones enunciados detectadas: ${enSections.length}`);
  const secByTit = {};
  for (const s of enSections) secByTit[s.tit] = (secByTit[s.tit] || 0) + 1;
  log(`Por titulación (enunciados): ${JSON.stringify(secByTit)}`);

  log(`Bloques de respuestas detectados: ${ansBlocks.length}`);
  const ansByTit = {};
  for (const b of ansBlocks) {
    const t = titleToTitulationKey(b.title);
    ansByTit[t] = (ansByTit[t] || 0) + 1;
  }
  log(`Por titulación (respuestas): ${JSON.stringify(ansByTit)}`);

  const imports = [];
  for (const sec of enSections) {
    const code = findTestCode(sec.chunk);
    if (!code) {
      log(`SKIP ${sec.tit}: no se encontró Código de Test`);
      continue;
    }
    let questions = parseExamQuestions(sec.chunk);
    if (!questions.length) {
      const preview = (sec.chunk.split("\n").slice(0, 12).join(" | ") || "").slice(0, 400);
      log(`SKIP ${sec.tit} T${code}: no se detectaron preguntas. Preview: ${preview}`);
      continue;
    }

    const ranges = {
      PNB: { min: 1, max: 27 },
      PER: { min: 1, max: 45 },
      PER_PNB: { min: 28, max: 45 },
      PY: { min: 1, max: 40 },
      CY: { min: 1, max: 40 }
    };
    const rr = ranges[sec.tit] || null;
    if (rr) {
      const seen = new Set();
      questions = questions
        .filter((q) => Number.isFinite(q.n) && q.n >= rr.min && q.n <= rr.max)
        .filter((q) => (seen.has(q.n) ? false : (seen.add(q.n), true)));
    }

    const ab = ansBlocks.find((b) => titleToTitulationKey(b.title) === sec.tit && b.code === code);
    if (!ab) {
      log(`SKIP ${sec.tit} T${code}: no se encontró bloque de respuestas`);
      continue;
    }

    const answersByNumber = {};
    for (const q of questions) {
      const a = ab.answers[q.n];
      if (a) answersByNumber[q.n] = a;
    }
    const have = Object.keys(answersByNumber).length;
    if (!have) {
      log(`SKIP ${sec.tit} T${code}: respuestas no mapean a los números de pregunta`);
      continue;
    }

    log(`OK ${sec.tit} T${code}: preguntas=${questions.length} respuestas=${have}`);
    imports.push({
      id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)),
      month: month ?? null,
      year: year ?? null,
      titulation: sec.tit,
      titulationLabel: sec.label,
      testCode: code,
      questions,
      answersByNumber,
      source: { enunciadosName: null, respuestasName: null }
    });
  }
  return imports;
}

async function generateExplanationsForTest({ testItem, log, setStatus, authToken }) {
  const token = String(authToken || "").trim();
  if (!token) throw new Error("Sesión requerida: el temario solo está disponible vía API (inicia sesión como admin).");

  const tit = testItem.titulation;
  if (!["PER", "PER_PNB", "PNB"].includes(tit)) throw new Error("Temario no disponible para esta titulación.");

  const pdfjsLib = await loadPdfjs();
  const byQn = {};

  const temarioCache = window.__temarioCache || (window.__temarioCache = new Map());

  for (const q of testItem.questions || []) {
    const correct = (testItem.answersByNumber || {})[q.n];
    const correctKey = correct ? String(correct).toLowerCase() : null;
    const optText = correctKey ? (q.options || []).find((o) => o.key === correctKey)?.text : "";
    const query = `${q.prompt || ""} ${optText || ""}`;
    const querySet = toks(query);

    const temarioId = temarioIdFor(q.topic, tit);
    if (!temarioId) {
      byQn[String(q.n)] = {
        quote: null,
        explain: `No hay temario asociado a este tema (${q.topic || "Sin tema"}).`,
        support: null,
        images: [],
        source: null
      };
      continue;
    }

    if (!temarioCache.has(temarioId)) {
      setStatus("Leyendo temario…");
      const apiUrl = `${API_BASE}/api/temario/${encodeURIComponent(temarioId)}`;
      log(`Cargando temario (API): ${temarioId}`);
      const pages = await loadPdfTextByPage(pdfjsLib, apiUrl, {
        headers: { authorization: `Bearer ${token}` }
      });
      const paras = [];
      for (let pi = 0; pi < pages.length; pi++) {
        for (const p of paragraphsFromPageText(pages[pi])) paras.push({ p, page: pi + 1 });
      }
      temarioCache.set(temarioId, paras);
    }

    const paras = temarioCache.get(temarioId);
    const scored = paras
      .map(({ p, page }) => ({ p, page, score: scorePara(querySet, p) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);

    const quote = scored[0]?.p || null;
    const support = scored.map((x) => x.p).join("\n\n") || null;
    const page = scored[0]?.page || null;

    byQn[String(q.n)] = {
      quote,
      explain: correct
        ? `Comparando el temario con las opciones, la correcta es ${String(correct).toUpperCase()}) ${optText || ""}.`
        : "No se encontró respuesta correcta para esta pregunta.",
      support,
      images: [],
      source: page ? { pdf: `temario:${temarioId}`, page } : { pdf: `temario:${temarioId}`, page: null }
    };
  }

  setStatus("Guardando explicaciones en API…");
  await saveAdminTestExplanations({
    authToken: token,
    testId: testItem.id,
    byQn,
    purgeMissing: true
  });
  log(`Explicaciones guardadas en API (Mongo) para test ${testItem.id}`);
  return true;
}

export async function generateExplanationsForTestItem({ testItem, log, setStatus, authToken }) {
  if (!testItem || typeof testItem !== "object") throw new Error("Missing testItem");
  if (!testItem.id) throw new Error("Missing test id");
  setStatus("Generando…");
  await generateExplanationsForTest({ testItem, log, setStatus, authToken });
  setStatus("Explicaciones OK");
  log(`Explicaciones generadas para ${testItem.titulation} T${testItem.testCode} (${testItem.questions?.length || 0} preguntas)`);
}

export async function importFromPdfs({ month, year, pdfEnunciados, pdfRespuestas, log, setStatus }) {
  const pdfjsLib = await loadPdfjs();
  setStatus("Leyendo PDFs…");

  log(`Enunciados: ${pdfEnunciados.name}`);
  log(`Respuestas: ${pdfRespuestas.name}`);

  const [{ pdf: enPdf, pages: enPages }, ansTextRaw] = await Promise.all([
    pdfToTextByPageForFile(pdfjsLib, pdfEnunciados),
    pdfToText(pdfjsLib, pdfRespuestas)
  ]);
  const enTextRaw = enPages.join("\n");
  const enText = normalize(enTextRaw);
  const ansText = normalize(ansTextRaw);

  log(`Enunciados chars: ${enText.length}`);
  log(`Respuestas chars: ${ansText.length}`);
  log(`Enunciados newlines: ${(enText.match(/\n/g) || []).length}`);
  log(`Respuestas newlines: ${(ansText.match(/\n/g) || []).length}`);
  log(`Muestras (enunciados) EXAMEN/Código/Test: ${JSON.stringify(sampleLinesContaining(enText, ["EXAMEN", "Código", "Test"], 6))}`);
  log(`Muestras (respuestas) Respuestas/EXAMEN/Código/Test: ${JSON.stringify(sampleLinesContaining(ansText, ["Respuestas", "EXAMEN", "Código", "Test"], 8))}`);

  setStatus("Parseando…");
  const resolved = resolveImportMonthYear({ formMonth: month, formYear: year, pdfEnunciados, pdfRespuestas, enText });
  if (resolved.month || resolved.year != null) {
    log(`Fecha examen (mes/año): ${resolved.month || "—"} / ${resolved.year != null ? resolved.year : "—"}`);
  }

  // Always build the canonical imports from text.
  // Then, *optionally* attach "resource pages" (images) after the last question using page boundaries.
  const imported = buildImports({ month: resolved.month, year: resolved.year, enText, ansText, log });

  const pageSections = detectExamSectionsByPage(enPages);
  log(`Secciones (por página) detectadas: ${pageSections.length}`);

  const byKey = new Map();
  for (const sec of pageSections) {
    if (!sec || !sec.tit || !sec.code) continue;
    byKey.set(`${sec.tit}|${sec.code}`, sec);
  }

  let resourcesAttached = 0;
  let resourcesTotal = 0;
  for (const t of imported) {
    const key = `${t.titulation}|${t.testCode}`;
    const sec = byKey.get(key) || null;
    if (!sec) {
      t.resources = [];
      continue;
    }

    const pagesForSection = enPages.slice(sec.startPageIdx, sec.endPageIdx + 1);
    const lastQPageRel = findLastQuestionPageIdx({ pages: pagesForSection, questions: t.questions || [] });
    const resourcePagesRel =
      lastQPageRel == null ? [] : [...Array(pagesForSection.length).keys()].filter((i) => i > lastQPageRel);
    const resourcePagesAbs = resourcePagesRel.map((i) => sec.startPageIdx + i + 1); // pdf.js pages are 1-based

    // Keep this bounded: resources tend to be 1-2 pages; avoid huge payloads.
    const bounded = resourcePagesAbs.slice(0, 6);
    const resources = bounded.length
      ? await renderPdfPagesToImages({ pdf: enPdf, pageNumbers: bounded, scale: 1.25, quality: 0.72 })
      : [];

    t.resources = resources;
    if (resources.length) resourcesAttached++;
    resourcesTotal += resources.length;
    log(
      `Recursos ${t.titulation} T${t.testCode}: lastQRel=${lastQPageRel ?? "—"} resources=${resources.length} pages=${bounded.join(",") || "—"}`
    );
  }

  log(`Recursos anexados: ${resourcesAttached}/${imported.length} (total páginas recurso: ${resourcesTotal})`);

  imported.forEach((x) => {
    x.source.enunciadosName = pdfEnunciados.name;
    x.source.respuestasName = pdfRespuestas.name;
  });
  setStatus(`Importados: ${imported.length}`);
  log(`Importados: ${imported.length}`);
  return imported;
}

export function importFromJsonText({ jsonText }) {
  const arr = safeJsonParse(jsonText || "[]", null);
  if (!Array.isArray(arr)) throw new Error("JSON no es array");
  return arr;
}

